import React, { useEffect, useMemo, useState } from 'react';
import {
  Dimmer,
  Button,
  Header,
  Icon,
  Label,
  Loader,
  Modal,
  Segment,
  Table,
} from 'semantic-ui-react';
import {
  ContractLineDetailsModalDto,
  ContractLineDto,
  contractService,
} from '../../services/contracts/ContractService';
import { campaignService } from '../../services/campaigns/campaignService';
import { WeeklyBonusContractLineDto } from '../../types/campaigns.types';
import { formatCnpj } from '../../services/utils/stringUtils';
import {
  getContractStatusSemanticColor,
  getContractStatusText,
  normalizeContractStatus,
} from '../../utils/contractStatusUtils';
import contractLineService from '../../services/contracts/ContractLineService';
import { useToast } from '../../hooks/useToast';
import { useMeuTimVerifyOnDemand } from '../../hooks/useMeuTimVerifyOnDemand';

/**
 * Fatia 0 (GCM) — verify Meu TIM on-demand no modal de detalhes das linhas (mesma
 * superfície reutilizada por Campanhas). Soft-fail: nunca bloqueia a visualização —
 * só informa o status real (ok / credencial inválida / captcha / timeout / erro).
 */
const MeuTimVerifyNowAction: React.FC<{ clientId: string }> = ({ clientId }) => {
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const { verifying, result, verifyNow } = useMeuTimVerifyOnDemand();

  const handleVerifyNow = async () => {
    const mapped = await verifyNow(clientId);
    if (mapped.status === 'ok') {
      showSuccess(mapped.message);
    } else if (mapped.status === 'credencial_invalida') {
      showError(mapped.message);
    } else if (mapped.status === 'bloqueio_ou_captcha') {
      showWarning(mapped.message);
    } else {
      showInfo(mapped.message);
    }
  };

  const statusColor =
    result?.status === 'ok'
      ? 'green'
      : result?.status === 'credencial_invalida'
      ? 'red'
      : result?.status === 'bloqueio_ou_captcha'
      ? 'yellow'
      : result
      ? 'grey'
      : undefined;

  return (
    <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Button
        size="mini"
        basic
        loading={verifying}
        disabled={verifying}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          void handleVerifyNow();
        }}
        title="Faz um login real no portal Meu TIM agora (até 45s) — não bloqueia esta tela."
      >
        <Icon name="sync" />
        Verificar acesso agora
      </Button>
      {result && (
        <Label size="mini" color={statusColor} title={result.message}>
          {result.status === 'ok' ? 'Acesso OK' : result.message}
        </Label>
      )}
    </span>
  );
};

/**
 * Fatia 1 (GCM) — ação de 1ª suspensão por linha, no modal de detalhes. Busca a
 * elegibilidade real no servidor (gate: time do contrato + status Ativo/Finalizado +
 * ainda não suspensa + health Meu TIM recente) e só oferece automação quando health Ok;
 * senão só o fallback manual (protocolo opcional). Nunca oferece re-suspensão.
 */
const GcmFirstSuspensionAction: React.FC<{ lineId: string; alreadySuspended?: boolean }> = ({
  lineId,
  alreadySuspended,
}) => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [healthOk, setHealthOk] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [done, setDone] = useState(Boolean(alreadySuspended));
  const [protocolo, setProtocolo] = useState('');

  useEffect(() => {
    if (alreadySuspended) {
      setDone(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    contractLineService
      .getGcmFirstSuspensionEligibility(lineId)
      .then((dto) => {
        if (cancelled) return;
        setEligible(dto.eligible);
        setHealthOk(dto.healthOk);
        setReason(dto.reason);
      })
      .catch(() => {
        if (!cancelled) setReason('Não foi possível carregar a elegibilidade.');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setChecked(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId, alreadySuspended]);

  if (done) {
    return (
      <Label color="green" size="small">
        <Icon name="check" />1ª suspensão OK
      </Label>
    );
  }

  if (loading) {
    return <Loader active inline size="mini" />;
  }

  if (!checked) {
    return <span style={{ color: '#999' }}>—</span>;
  }

  if (!eligible) {
    return (
      <Label size="small" basic title={reason ?? undefined}>
        Não elegível
      </Label>
    );
  }

  const handleEnqueueAutomation = async () => {
    setActionBusy(true);
    try {
      const result = await contractLineService.enqueueGcmFirstSuspensionAutomation(lineId);
      if (result.success) {
        showSuccess(result.message);
        setDone(true);
      } else {
        showError(result.message);
      }
    } catch (err: any) {
      showError(err?.response?.data?.message || err?.message || 'Não foi possível enfileirar a automação.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleMarkManual = async () => {
    setActionBusy(true);
    try {
      const result = await contractLineService.markGcmFirstSuspensionManual(lineId, {
        protocolo: protocolo.trim() || undefined,
      });
      if (result.success) {
        showSuccess(result.message);
        setDone(true);
      } else {
        showError(result.message);
      }
    } catch (err: any) {
      showError(err?.response?.data?.message || err?.message || 'Não foi possível registrar a 1ª suspensão.');
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
      {healthOk ? (
        <Button
          size="mini"
          color="blue"
          loading={actionBusy}
          disabled={actionBusy}
          onClick={() => void handleEnqueueAutomation()}
        >
          Enfileirar automação
        </Button>
      ) : (
        <Label size="mini" color="grey" title="Sem verify Meu TIM Ok recente — use o registro manual.">
          Automação indisponível
        </Label>
      )}
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          type="text"
          placeholder="Protocolo (opcional)"
          value={protocolo}
          onChange={(e) => setProtocolo(e.target.value)}
          style={{ width: 110, fontSize: 12, padding: '2px 4px' }}
        />
        <Button size="mini" basic loading={actionBusy} disabled={actionBusy} onClick={() => void handleMarkManual()}>
          Marcar manual
        </Button>
      </div>
    </div>
  );
};

export type ContractLinesCampaignContext = {
  campaignId: string;
  userId?: string;
  teamId?: string;
  revenueBucket?: 'Aprovada' | 'EmAprovacao';
  weeklyBonus?: {
    windowIndex: number;
    userId: string;
  };
};

type ContractLinesViewModalProps = {
  open: boolean;
  onClose: () => void;
  contractId: string;
  contractInfo?: {
    clientRazaoSocial?: string;
    clientCnpj?: string;
    groupId?: string | null;
    isGroupParent?: boolean;
  };
  campaignContext?: ContractLinesCampaignContext;
};

function lineIsAditivo(line: ContractLineDto): boolean {
  const ct = (line as ContractLineDto & { contractType?: string }).contractType;
  return line.negociacao === 'Aditivo' || ct === 'Aditivo';
}

type GroupedCampaignLine = {
  key: string;
  line: ContractLineDto;
  quantidade: number;
  valorTotal: number;
  pontos: number;
  hasSuspension: boolean;
};

function buildLineGroupKey(line: ContractLineDto): string {
  const plan = line.planId ?? line.planName ?? line.plano ?? '';
  const negociacao = line.negociacao ?? '';
  const tipoVenda = line.tipoVenda ?? '';
  const valorUnit = line.valorMensal ?? 0;
  return `${negociacao}|${plan}|${tipoVenda}|${valorUnit}`;
}

function groupCampaignLines(
  lines: ContractLineDto[],
  pointsByLineId: Map<string, { pontos: number; receitaElegivel: number }>,
): GroupedCampaignLine[] {
  const groups = new Map<string, GroupedCampaignLine>();

  for (const line of lines) {
    if (!pointsByLineId.has(line.id)) continue;

    const eligible = pointsByLineId.get(line.id)!;
    const qty = line.quantidade ?? 1;
    const valor =
      eligible.receitaElegivel > 0
        ? eligible.receitaElegivel
        : (line.valorTotalMensal ?? (line.valorMensal ?? 0) * qty);
    const key = buildLineGroupKey(line);
    const isMarkedForSuspension = !!(line.excluirDoContrato || line.requerSuspensao);

    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        key,
        line,
        quantidade: qty,
        valorTotal: valor,
        pontos: eligible.pontos,
        hasSuspension: isMarkedForSuspension,
      });
    } else {
      existing.quantidade += qty;
      existing.valorTotal += valor;
      existing.pontos += eligible.pontos;
      existing.hasSuspension = existing.hasSuspension || isMarkedForSuspension;
    }
  }

  return Array.from(groups.values());
}

function renderFlatLineRow(line: ContractLineDto, rowKey: string, showGcmFirstSuspension: boolean) {
  const isMarkedForSuspension = !!(line.excluirDoContrato || line.requerSuspensao);
  const isAditivo = lineIsAditivo(line);
  const ext = line as ContractLineDto & { isEsim?: boolean };

  return (
    <Table.Row key={rowKey} warning={isMarkedForSuspension}>
      <Table.Cell textAlign="center">
        {isMarkedForSuspension ? (
          <Label color="red" size="small">A cancelar</Label>
        ) : (
          <Label color="green" size="small">Ativa</Label>
        )}
      </Table.Cell>
      <Table.Cell>
        <Label color={isAditivo ? 'green' : 'blue'} size="small">
          {line.negociacao ?? '—'}
        </Label>
      </Table.Cell>
      <Table.Cell>{line.ddd ?? '—'}</Table.Cell>
      <Table.Cell>
        <strong>{line.numero ?? '—'}</strong>
      </Table.Cell>
      <Table.Cell>{line.planName || line.plano || 'N/A'}</Table.Cell>
      <Table.Cell>{line.quantidade ?? 1}</Table.Cell>
      <Table.Cell>
        R${' '}
        {(line.valorTotalMensal ?? line.valorMensal ?? 0).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </Table.Cell>
      <Table.Cell>{line.tipoVenda ?? '—'}</Table.Cell>
      <Table.Cell>
        <code>{line.chipCcid ?? '—'}</code>
        {ext.isEsim ? (
          <Label size="mini" style={{ marginLeft: 6 }}>e-SIM</Label>
        ) : null}
      </Table.Cell>
      <Table.Cell>
        {line.ehPortabilidade ? (
          <span style={{ fontSize: '0.85em' }}>{line.portabilidadeOperadora ?? 'Sim'}</span>
        ) : (
          '—'
        )}
      </Table.Cell>
      {showGcmFirstSuspension && (
        <Table.Cell>
          <GcmFirstSuspensionAction lineId={line.id} alreadySuspended={line.estaSuspensa} />
        </Table.Cell>
      )}
    </Table.Row>
  );
}

function renderGroupedLineRow(group: GroupedCampaignLine) {
  const line = group.line;
  const isAditivo = lineIsAditivo(line);

  return (
    <Table.Row key={group.key} warning={group.hasSuspension}>
      <Table.Cell textAlign="center">
        {group.hasSuspension ? (
          <Label color="red" size="small">A cancelar</Label>
        ) : (
          <Label color="green" size="small">Ativa</Label>
        )}
      </Table.Cell>
      <Table.Cell>
        <Label color={isAditivo ? 'green' : 'blue'} size="small">
          {line.negociacao ?? '—'}
        </Label>
      </Table.Cell>
      <Table.Cell>{line.planName || line.plano || 'N/A'}</Table.Cell>
      <Table.Cell>{group.quantidade}</Table.Cell>
      <Table.Cell>
        R{' '}
        {group.valorTotal.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </Table.Cell>
      <Table.Cell>{line.tipoVenda ?? '—'}</Table.Cell>
      <Table.Cell>
        {line.ehPortabilidade ? (
          <span style={{ fontSize: '0.85em' }}>{line.portabilidadeOperadora ?? 'Sim'}</span>
        ) : (
          '—'
        )}
      </Table.Cell>
      <Table.Cell>
        <Label color="orange" size="small">
          <Icon name="star" />
          {group.pontos.toFixed(1)}
        </Label>
      </Table.Cell>
    </Table.Row>
  );
}

function renderContractBlock(
  dto: ContractLineDetailsModalDto,
  campaignMode: boolean,
  pointsByLineId: Map<string, { pontos: number; receitaElegivel: number }>,
  showGcmFirstSuspension: boolean,
  showMeuTimVerify: boolean,
) {
  const lines = dto.linhas ?? [];
  const status = normalizeContractStatus(dto.status);
  const groupedLines = campaignMode
    ? groupCampaignLines(lines, pointsByLineId)
    : [];

  return (
    <Segment key={dto.id} style={{ marginBottom: 16 }}>
      <Header as="h4">
        {dto.clientRazaoSocial ?? 'Cliente'}
        <Header.Subheader>
          CNPJ: {formatCnpj(dto.clientCnpj ?? '')}
          {dto.id ? (
            <span style={{ marginLeft: 8 }}>
              · Contrato <code>{dto.id.slice(0, 8)}</code>
            </span>
          ) : null}
          {status !== null && status !== undefined ? (
            <Label
              color={getContractStatusSemanticColor(status)}
              style={{ marginLeft: 8 }}
              size="small"
            >
              {getContractStatusText(status)}
            </Label>
          ) : null}
          {dto.fastchip ? (
            <Label color="blue" size="small" style={{ marginLeft: 8 }}>
              Fastchip
            </Label>
          ) : null}
          {dto.vtmeAssignedName ? (
            <Label color="teal" size="small" style={{ marginLeft: 8 }}>
              VTME: {dto.vtmeAssignedName}
            </Label>
          ) : null}
          {showMeuTimVerify && dto.clientId ? (
            <MeuTimVerifyNowAction clientId={dto.clientId} />
          ) : null}
        </Header.Subheader>
      </Header>
      <div style={{ overflowX: 'auto', maxHeight: '48vh' }}>
        <Table celled striped compact size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Negociação</Table.HeaderCell>
              {campaignMode ? null : (
                <>
                  <Table.HeaderCell>DDD</Table.HeaderCell>
                  <Table.HeaderCell>Número</Table.HeaderCell>
                </>
              )}
              <Table.HeaderCell>Plano</Table.HeaderCell>
              <Table.HeaderCell>Qtd</Table.HeaderCell>
              <Table.HeaderCell>Valor</Table.HeaderCell>
              <Table.HeaderCell>Tipo venda</Table.HeaderCell>
              {campaignMode ? null : <Table.HeaderCell>ICCID</Table.HeaderCell>}
              <Table.HeaderCell>Portabilidade</Table.HeaderCell>
              {campaignMode ? <Table.HeaderCell>Pontos</Table.HeaderCell> : null}
              {showGcmFirstSuspension && <Table.HeaderCell>1ª suspensão</Table.HeaderCell>}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {campaignMode ? (
              groupedLines.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={8} textAlign="center">
                    Nenhuma linha elegível neste contrato
                  </Table.Cell>
                </Table.Row>
              ) : (
                groupedLines.map((group) => renderGroupedLineRow(group))
              )
            ) : lines.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={showGcmFirstSuspension ? 11 : 10} textAlign="center">
                  Nenhuma linha cadastrada
                </Table.Cell>
              </Table.Row>
            ) : (
              lines.map((line, idx) =>
                renderFlatLineRow(line, `${dto.id}-${line.id}-${idx}`, showGcmFirstSuspension),
              )
            )}
          </Table.Body>
        </Table>
      </div>
    </Segment>
  );
}

export const ContractLinesViewModal: React.FC<ContractLinesViewModalProps> = ({
  open,
  onClose,
  contractId,
  contractInfo,
  campaignContext,
}) => {
  const [loading, setLoading] = useState(false);
  const [blocks, setBlocks] = useState<ContractLineDetailsModalDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [eligibleLines, setEligibleLines] = useState<
    Array<{ lineId: string; pontos: number; receitaElegivel: number }>
  >([]);
  const [weeklyBonusLines, setWeeklyBonusLines] = useState<WeeklyBonusContractLineDto[]>([]);

  const weeklyBonusMode = Boolean(campaignContext?.weeklyBonus);
  const campaignMode = Boolean(campaignContext?.campaignId) && !weeklyBonusMode;
  // Fatia 1 (GCM) só faz sentido no fluxo "detalhes das linhas" puro — não em campanhas/bônus.
  const showGcmFirstSuspension = !campaignMode && !weeklyBonusMode;
  // Fatia 0 (GCM) — verify Meu TIM on-demand: mesma superfície "detalhes das linhas" puro.
  const showMeuTimVerify = !campaignMode && !weeklyBonusMode;

  const pointsByLineId = useMemo(() => {
    const map = new Map<string, { pontos: number; receitaElegivel: number }>();
    for (const row of eligibleLines) {
      map.set(row.lineId, { pontos: row.pontos, receitaElegivel: row.receitaElegivel });
    }
    return map;
  }, [eligibleLines]);

  useEffect(() => {
    if (!open || !contractId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setBlocks([]);
      setEligibleLines([]);
      setWeeklyBonusLines([]);

      try {
        if (weeklyBonusMode && campaignContext?.weeklyBonus) {
          const lines = await campaignService.getWeeklyBonusContractLines(
            campaignContext.campaignId,
            campaignContext.weeklyBonus.windowIndex,
            campaignContext.weeklyBonus.userId,
            contractId,
          );
          if (!cancelled) setWeeklyBonusLines(lines);
          return;
        }

        if (campaignContext?.campaignId) {
          const eligible = await campaignService.getEligibleContractLines(
            campaignContext.campaignId,
            contractId,
            {
              userId: campaignContext.userId,
              teamId: campaignContext.teamId,
              bucket: campaignContext.revenueBucket,
            },
          );
          if (!cancelled) setEligibleLines(eligible);
        }

        const useGroup =
          contractInfo?.isGroupParent && contractInfo?.groupId;

        if (useGroup) {
          const group = await contractService.getGroupLineDetailsModal(contractInfo.groupId!);
          if (!cancelled) setBlocks(group ?? []);
        } else {
          const modalData = await contractService.getContractForLineDetailsModal(contractId);
          if (!cancelled) setBlocks([modalData]);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Erro ao carregar linhas do contrato:', e);
          setError('Não foi possível carregar as linhas do contrato.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    contractId,
    contractInfo?.groupId,
    contractInfo?.isGroupParent,
    campaignContext?.campaignId,
    campaignContext?.userId,
    campaignContext?.teamId,
    campaignContext?.revenueBucket,
    campaignContext?.weeklyBonus?.windowIndex,
    campaignContext?.weeklyBonus?.userId,
    weeklyBonusMode,
  ]);

  const headerTitle =
    contractInfo?.clientRazaoSocial?.trim() ||
    blocks[0]?.clientRazaoSocial?.trim() ||
    'Detalhes das linhas';

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="list" />
        Linhas do contrato — {headerTitle}
      </Modal.Header>
      <Modal.Content scrolling style={{ minHeight: 200 }}>
        <Dimmer active={loading} inverted>
          <Loader>Carregando linhas...</Loader>
        </Dimmer>
        {error && !loading ? (
          <Segment placeholder>
            <Header icon>
              <Icon name="warning sign" />
              {error}
            </Header>
          </Segment>
        ) : null}
        {weeklyBonusMode && !loading && !error ? (
          <div style={{ overflowX: 'auto' }}>
            <Table celled striped compact size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Negociação</Table.HeaderCell>
                  <Table.HeaderCell>Plano</Table.HeaderCell>
                  <Table.HeaderCell>Portabilidade</Table.HeaderCell>
                  <Table.HeaderCell>Valor linha</Table.HeaderCell>
                  <Table.HeaderCell>Tier</Table.HeaderCell>
                  <Table.HeaderCell>Bonificação</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {weeklyBonusLines.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={6} textAlign="center">
                      Nenhuma linha qualificada neste contrato
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  weeklyBonusLines.map((line) => (
                    <Table.Row key={line.lineId}>
                      <Table.Cell>{line.negociacao ?? '—'}</Table.Cell>
                      <Table.Cell>{line.plano ?? '—'}</Table.Cell>
                      <Table.Cell>{line.ehPortabilidade ? 'Sim' : 'Não'}</Table.Cell>
                      <Table.Cell>
                        R$ {line.valorLinha.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </Table.Cell>
                      <Table.Cell>{line.tierDescricao}</Table.Cell>
                      <Table.Cell>
                        <strong>
                          R$ {line.bonificacao.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </strong>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>
          </div>
        ) : null}
        {!weeklyBonusMode && !loading && !error && blocks.length === 0 ? (
          <Segment placeholder>
            <Header icon>
              <Icon name="list" />
              Nenhuma linha encontrada
            </Header>
          </Segment>
        ) : null}
        {!weeklyBonusMode &&
          !loading &&
          blocks.map((dto) =>
            renderContractBlock(
              dto,
              campaignMode,
              pointsByLineId,
              showGcmFirstSuspension,
              showMeuTimVerify,
            ),
          )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Fechar</Button>
      </Modal.Actions>
    </Modal>
  );
};
